import React, { useState } from 'react';
import {
  FaUmbrellaBeach, FaShieldAlt, FaChartLine, FaCheckCircle,
  FaChevronDown, FaCoins, FaHeartbeat, FaBalanceScale, FaLandmark
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
const PostRetirementSolutions = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What are Post-Retirement Solutions, and how do they differ from retirement planning?",
      a: "While retirement planning focuses on *accumulating* a corpus during your working years, post-retirement solutions focus on *decumulation*—managing, protecting, and distributing that accumulated wealth to generate reliable, tax-efficient income throughout your retirement years."
    },
    {
      q: "How can I ensure my monthly cash flow matches my pre-retirement lifestyle?",
      a: "We structure a personalized income distribution model using tools like Systematic Withdrawal Plans (SWPs) from balanced mutual funds, dividend yields, senior citizen savings schemes, and fixed-income annuities to ensure your monthly cash flow is steady and dependable."
    },
    {
      q: "How do you handle medical and healthcare inflation post-retirement?",
      a: "Healthcare costs escalate faster than general inflation. We ring-fence a portion of your wealth into dedicated medical contingency reserves, comprehensive senior health insurance policies, and critical illness top-ups to protect your core savings from hospital bills."
    },
    {
      q: "Can you help transition my wealth to my children seamlessly?",
      a: "Yes. Post-retirement solutions heavily integrate estate planning, including Will drafting, nomination audits, and family trust structuring, to ensure your assets are passed down smoothly without probate delays or family disputes."
    }
  ];

  return (
    <main className="post-retirement-solutions-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Post Retirement Solutions in Hyderabad | Retirement Income Planning | Lotoswealth</title>

        <meta
          name="description"
          content="Lotoswealth provides expert Post Retirement Solutions in Hyderabad, helping retirees generate regular income, preserve wealth, manage investments, optimize taxes, and achieve financial security through personalized retirement income planning and wealth management strategies."
        />

        <meta
          name="keywords"
          content="Post Retirement Solutions Hyderabad, Post Retirement Planning Hyderabad, Retirement Income Planning Hyderabad, Retirement Income Solutions Hyderabad, Wealth Management Hyderabad, Senior Citizen Financial Planning Hyderabad, Pension Planning Hyderabad, Retirement Investment Planning Hyderabad, Retirement Wealth Management Hyderabad, Financial Planning Hyderabad, Investment Advisor Hyderabad, Financial Advisor Hyderabad, Tax Planning Hyderabad, Asset Management Hyderabad, Fixed Income Investments Hyderabad, Capital Preservation Hyderabad, Retirement Portfolio Management Hyderabad, Senior Citizen Investment Hyderabad, Wealth Preservation Hyderabad, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link
          rel="canonical"
          href="https://www.lotoswealth.com/post-retirement-solutions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta
          property="og:url"
          content="https://www.lotoswealth.com/post-retirement-solutions"
        />
        <meta
          property="og:title"
          content="Post Retirement Solutions in Hyderabad | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Secure your retirement with expert Post Retirement Solutions in Hyderabad. Lotoswealth offers retirement income planning, wealth preservation, tax-efficient investment strategies, and personalized financial advisory."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Post Retirement Solutions in Hyderabad | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Enjoy financial peace of mind after retirement with Post Retirement Solutions in Hyderabad. Expert retirement income planning, wealth preservation, investment management, and financial advisory."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>

        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img
            src="/images/post-retirement-solutions.jpg"
            alt="Post-Retirement Solutions"
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Post-Retirement Solutions
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Turn Your Accumulated Corpus Into a Lifelong, Reliable Income
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. Reaching retirement is a major milestone, but managing your wealth *after* your career ends requires an entirely different financial strategy. Our Post-Retirement Solutions focus on intelligent asset decumulation, ensuring your hard-earned corpus continues to support your lifestyle, fight inflation, and provide absolute peace of mind.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            We specialize in transforming lump-sum retirement savings into structured monthly cash flows using tax-efficient Systematic Withdrawal Plans (SWPs), senior-friendly debt instruments, and dynamic asset rebalancing designed to protect you from market downturns.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Enjoy your golden years with complete financial independence, knowing your cash flow is secure and your legacy is protected.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>

            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY POST-RETIREMENT MANAGEMENT MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                The Critical Shift: Accumulation to Decumulation
              </h2>
              <p className="text-muted line-height-lg">
                During your working life, the focus is on accumulation—saving and growing your assets. Once retired, the paradigm shifts entirely to decumulation. Managing withdrawals incorrectly can lead to prematurely exhausting your life savings.
              </p>
              <p className="text-muted line-height-lg">
                Post-retirement solutions provide a structured framework to withdraw funds efficiently, minimize tax outflows on pension and investment income, and safeguard your lifestyle against sequence-of-returns risk during market downturns.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Key Focus Areas</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Reliable Cash Flow:</strong> Automated monthly payouts matching your lifestyle.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Tax Minimization:</strong> Structuring withdrawals to lower tax liabilities.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Capital Longevity:</strong> Ensuring your money outlives you, not vice versa.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Liquidity Management:</strong> Keeping emergency funds ready without breaking growth assets.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR POST-RETIREMENT SERVICES (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>

        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">💼 Our Post-Retirement Solutions</h2>
            <p className="text-white">Specialized advisory services tailored for retirees and senior citizens.</p>
          </div>

          <div className="row g-4">

            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaCoins size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Systematic Withdrawal Plans (SWP)</h5>
                <p className="text-muted small">Set up disciplined, tax-efficient monthly income streams from your mutual fund investments while letting the remainder continue to compound.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaLandmark size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Senior Citizen Savings & Annuities</h5>
                <p className="text-muted small">Optimize allocations in government-backed senior schemes (SCSS), PMVVY, and secure private annuities to establish a guaranteed baseline income.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaHeartbeat size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Healthcare Cost Mitigation</h5>
                <p className="text-muted small">Establish dedicated medical emergency funds and audit health insurance policies to ensure high-ticket hospital bills never disrupt your monthly budget.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBalanceScale size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Tax-Efficient Income Structuring</h5>
                <p className="text-muted small">Navigate tax slabs skillfully by combining interest income, capital gains, and tax-free withdrawals to maximize your in-hand post-retirement funds.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Bucket Strategy Portfolio Design</h5>
                <p className="text-muted small">Divide your corpus into short-term cash buckets, medium-term income buckets, and long-term growth buckets to weather market cycles safely.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaChartLine size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Estate & Legacy Transition</h5>
                <p className="text-muted small">Coordinate smooth asset transfers, Will executions, and beneficiary updates to ensure your family experiences zero legal friction later.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP IMPLEMENTATION PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 Our Post-Retirement Implementation Process</h2>
            <p className="text-light opacity-75">A structured approach to managing your wealth after your career ends.</p>
          </div>

          <div className="row g-4">

            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Corpus Audit & Mapping</h5>
                <p className="small text-muted mb-0">We evaluate your total accumulated retirement savings across EPF, PPF, mutual funds, real estate, and bank deposits.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – Cash Flow Designing</h5>
                <p className="small text-muted mb-0">Mapping your mandatory monthly expenses against secure income streams to guarantee baseline financial comfort.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Bucket Allocation</h5>
                <p className="small text-muted mb-0">Setting up the multi-bucket portfolio structure to balance immediate liquidity needs with long-term inflation beating growth.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – Automated Payout Setup</h5>
                <p className="small text-muted mb-0">Executing SWPs, dividend redirection, and pension configurations so your monthly income flows smoothly without hassle.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Periodic Review & Support</h5>
                <p className="small text-muted mb-0">Ongoing check-ins with our advisors to adjust withdrawal rates, review health contingencies, and adapt to changing tax laws.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">

            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Benefits from Post-Retirement Solutions?</h3>
              <p className="text-muted small mb-4">Our decumulation frameworks are built specifically for:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "Recent Retirees", "Senior Citizens Seeking Regular Income",
                  "Individuals with Lump-Sum Provident Funds", "Retirees Facing High Tax Brackets",
                  "Those Worried About Outliving Savings", "NRIs Returning to Retire in India",
                  "Spouses Managing Inherited Wealth", "Stable Income Seekers"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Trust Lotoswealth?</h3>
                <p className="text-muted small mb-4">Managing income post-retirement requires empathy, absolute accuracy, and reliable support. Clients choose us because we offer:</p>
                <div className="row g-3">
                  {[
                    "Guaranteed Monthly Cash Flow Designs", "Tax-Optimized Withdrawal Structuring",
                    "Dedicated Senior Relationship Managers", "Capital Protection Focus",
                    "Inflation-Resilient Portfolio Frameworks", "Seamless Healthcare Buffer Planning",
                    "Transparent, Honest Guidance", "Complete Family Estate Integration"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Post-Retirement Cash Flows & Management</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Ready to Structure Your Post-Retirement Income?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Let us help you transform your retirement savings into a dependable, stress-free monthly paycheck. Connect with our experts today.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Schedule a Post-Retirement Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default PostRetirementSolutions;